import React from 'react';
import { Heart, AlertCircle, CheckCircle2, Flame, Wind, Thermometer, Dumbbell, Wine, Smile, BookOpen } from 'lucide-react';
import { FadeIn } from './Shared';
export const PersonalNote = () => <section className="py-20 bg-cream" data-id="element-11">
    <div className="max-w-3xl mx-auto px-6" data-id="element-12">
      <FadeIn data-id="element-13">
        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-terracotta/10 relative" data-id="element-14">
          <Heart className="absolute top-8 right-8 w-8 h-8 text-terracotta/20" data-id="element-15" />
          <h3 className="text-sm font-bold tracking-widest text-terracotta uppercase mb-4" data-id="element-16">
            Una nota personal
          </h3>
          <h2 className="text-3xl md:text-4xl font-playfair font-bold mb-6 text-brown" data-id="element-17">
            De Mujer a Mujer
          </h2>

          <div className="space-y-4 text-lg text-brown/80 leading-relaxed" data-id="element-18">
            <p data-id="element-19">
              Si anoche te despertaste a las 3:00 AM empapada en sudor, con el
              corazón galopando y una frustración que te daban ganas de llorar,
              quiero que sepas algo:{' '}
              <strong data-id="element-20">No estás loca y no estás sola.</strong>
            </p>
            <p data-id="element-21">
              Vivir en Estados Unidos con menopausia es un reto doble. El ritmo
              aquí no se detiene porque tengas un sofoco en medio de una reunión
              de trabajo o mientras manejas en el tráfico.
            </p>
            <p data-id="element-22">
              Este protocolo nació porque yo también estuve ahí, buscando
              soluciones en Google a medianoche. Lo que tienes en tus manos no
              es teoría; es un sistema de "supervivencia y dominio" diseñado
              para la mujer real que trabaja, cuida a su familia y necesita
              resultados hoy mismo.
            </p>
            <p className="font-playfair text-xl text-terracotta italic pt-4" data-id="element-23">
              Bienvenida al camino de regreso a tu centro.
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>;
export const Introduction = () => <section className="py-20 bg-brown text-cream" data-id="element-24">
    <div className="max-w-4xl mx-auto px-6" data-id="element-25">
      <FadeIn data-id="element-26">
        <h2 className="text-3xl md:text-5xl font-playfair font-bold mb-8 text-center text-gold" data-id="element-27">
          El Código del Incendio Interno
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-28">
          <div data-id="element-29">
            <p className="text-lg leading-relaxed mb-6" data-id="element-30">
              El error #1 es tratar el sofoco (el bochorno) como un enemigo
              externo que llega de la nada. En realidad, es un error de
              comunicación de tu hipotálamo (el termostato de tu cerebro).
            </p>
            <p className="text-lg leading-relaxed" data-id="element-31">
              Debido a la caída de estrógenos, tu cerebro se ha vuelto
              "hipersensible". Cualquier pequeño cambio en tu azúcar, tu estrés
              o tu ambiente dispara la alarma de incendios.
            </p>
          </div>

          <div className="bg-white/5 p-8 rounded-2xl border border-white/10" data-id="element-32">
            <h3 className="text-xl font-bold mb-6 text-gold flex items-center gap-2" data-id="element-33">
              <AlertCircle className="w-6 h-6" data-id="element-34" />
              ¿Por qué ha fallado lo que intentaste?
            </h3>
            <ul className="space-y-4" data-id="element-35">
              <li className="flex gap-3" data-id="element-36">
                <span className="text-terracotta font-bold" data-id="element-37">•</span>
                <span data-id="element-38">
                  <strong data-id="element-39">Remedios aislados:</strong> Tomar una vitamina sin
                  cambiar el desayuno es como querer apagar un incendio forestal
                  con un vaso de agua.
                </span>
              </li>
              <li className="flex gap-3" data-id="element-40">
                <span className="text-terracotta font-bold" data-id="element-41">•</span>
                <span data-id="element-42">
                  <strong data-id="element-43">Atacar solo el síntoma:</strong> Poner el aire
                  acondicionado al máximo enfría tu piel, pero no apaga la
                  caldera interna que se encendió en tus células.
                </span>
              </li>
              <li className="flex gap-3" data-id="element-44">
                <span className="text-terracotta font-bold" data-id="element-45">•</span>
                <span data-id="element-46">
                  <strong data-id="element-47">Resignación:</strong> Creer que "así es la edad". Tu
                  calidad de vida no es negociable.
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center bg-terracotta/20 p-6 rounded-xl border border-terracotta/30" data-id="element-48">
          <p className="text-xl font-playfair" data-id="element-49">
            <strong data-id="element-50">El Mecanismo de las 6 Anclas:</strong> Aprenderás a usar 6
            pilares biológicos (Glucemia, Nervios, Entorno, Metabolismo, Toxinas
            y Emociones) para que el sofoco simplemente pierda el poder de
            encenderse.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const chaptersData = [{
  id: 1,
  title: 'ANCLA GLUCÉMICA',
  subtitle: 'Apagando la Caldera',
  icon: Flame,
  objective: 'Sabrás exactamente cómo comer para evitar que los picos de azúcar disparen el calor interno.',
  concept: 'Cada vez que tu azúcar en sangre sube y baja bruscamente (pico de glucosa), tu cuerpo libera adrenalina. La adrenalina es el "fósforo" que enciende el bochorno. En la menopausia, somos menos tolerantes al azúcar.',
  strategy: {
    name: 'El Desayuno Pared',
    text: 'Para frenar los sofocos matutinos, tu primera comida del día debe ser una "pared" de proteína y grasa. Si desayunas solo avena con fruta o un café con pan dulce, tendrás un sofoco a las 10:00 AM garantizado.',
    bad: 'Café con azúcar + bagel o cereal (Caldera encendida).',
    good: '2 huevos con espinacas y 1/4 de aguacate (Termostato estable).'
  },
  insight: 'La glucosa inestable es el combustible #1 de los sudores nocturnos. Si cenas pasta o postre, Prepárate para sudar a las 2 AM.',
  checklist: ['Mañana comeré proteína sólida (huevos, pollo, pavo) antes de probar cualquier carbohidrato.', 'Solo beberé café después de haber desayunado algo de proteína.', 'Sustituiré el jugo de naranja por la fruta entera (la fibra frena el pico de calor).'],
  worksheet: {
    title: 'Mi Desayuno de Poder',
    fields: ['Mañana desayunaré:', 'Sustituiré mi café azucarado por:']
  }
}, {
  id: 2,
  title: 'ANCLA NERVIOSA',
  subtitle: 'El Reset de 2 Minutos',
  icon: Wind,
  objective: 'Detener un sofoco en seco usando tu respiración como interruptor biológico.',
  concept: 'El sofoco activa el sistema "Luchar o Huir". Tu corazón se acelera y entras en pánico. Esto expande los vasos sanguíneos y el calor sube. Si engañamos al cerebro haciéndole creer que estamos en paz total, el mensaje de calor se cancela.',
  strategy: {
    name: 'Respiración de Enfriamiento (4-8)',
    text: 'Inhala por la nariz en 4 segundos contando mentalmente. Exhala por la boca muy lento, frunciendo los labios (como si soplaras un pitillo/popote) durante 8 segundos. La exhalación larga es la señal química que apaga la alarma de incendio del cerebro.'
  },
  checklist: ['Practicaré la respiración 4-8 tres veces al día, aunque no tenga calor.', 'Me mojaré las muñecas con agua fría al sentir la primera señal de calor.', 'Llevaré un abanico de mano en mi bolsa como "seguro de vida" emocional.']
}, {
  id: 3,
  title: 'ANCLA TÉRMICA',
  subtitle: 'Hackeando el Entorno (USA Edition)',
  icon: Thermometer,
  objective: 'Configurar tu casa y tu ropa para que el ambiente trabaje a tu favor, no en tu contra.',
  concept: 'Vivir en USA significa lidiar con calefacciones centrales secas o aires acondicionados agresivos. Tu cuerpo necesita una temperatura estable para que el hipotálamo no se confunda.',
  strategy: {
    name: 'Estrategias Pro',
    text: 'La Regla de las Capas: Vístete como cebolla. Camiseta de algodón, luego suéter ligero. Nunca uses lana o poliéster directamente sobre la piel; son plásticos que atrapan el calor.\n\nHigiene de Sueño Térmico: Mantén el termostato a 67°F (19°C). Usa sábanas de 100% algodón o lino. El satén y el poliéster son trampas de sudor.\n\nEl Truco del Hielo en la Nuca: Tener un "ice pack" listo en el congelador. Ponerlo en la base del cráneo detiene la señal del sofoco en segundos.'
  },
  checklist: ['Revisar que mi pijama sea de fibras naturales (algodón).', 'Colocar un ventilador pequeño directo a mi lado de la cama.', 'Dejar un vaso con agua fría y hielos listo antes de dormir.']
}, {
  id: 4,
  title: 'ANCLA METABÓLICA',
  subtitle: 'El Movimiento que no Estresa',
  icon: Dumbbell,
  objective: 'Entrenar para quemar azúcar sin elevar demasiado el cortisol.',
  concept: 'El cardio intenso (correr, zumba fuerte) eleva el cortisol, lo cual dispara sofocos. Lo que necesitas es músculo. El músculo es como una esponja que absorbe el azúcar de tu sangre. Más músculo = menos azúcar libre = menos sofocos.',
  strategy: {
    name: 'Fuerza Controlada',
    text: 'Sentadillas, usar una silla para subir y bajar, o empujar la pared. 15 minutos, 3 veces por semana es suficiente para cambiar tu metabolismo.'
  },
  checklist: ['Caminaré siempre 10 minutos después de la cena para "quemar" la glucosa antes de ir al colchón.', 'Haré 3 series de 10 sentadillas lunes, miércoles y viernes.', 'Evitaré el ejercicio agotador después de las 7:00 PM.']
}, {
  id: 5,
  title: 'ANCLA TÓXICA',
  subtitle: 'Los Detonantes Ocultos',
  icon: Wine,
  objective: 'Identificar y eliminar los "fósforos" que encienden tu caldera interna desde adentro.',
  concept: 'Existen sustancias vasodilatadoras que abren tus venas y suben la sangre a la cara. En Estados Unidos, los procesados están llenos de esto.',
  strategy: {
    name: 'Los Villanos #1',
    text: 'Vino Tinto por la noche: Es veneno para los sofocos nocturnos. Relaja los vasos y dispara el sudor a las 2 horas.\n\nExceso de Sal (Sodio): La comida congelada y de restaurante en USA tiene demasiada sal. Esto altera tu presión y tu temperatura.\n\nCafeína después de las 2 PM: La cafeína tarda 8 horas en salir de tu sistema. Si tomas café tarde, tu sistema nervioso estará "alerta" y disparará calor mientras intentas dormir.'
  },
  checklist: ['Eliminaré el alcohol por los próximos 14 días para ver la diferencia real.', 'Sustituiré la sal común por sal de mar en menores cantidades.', 'Cambiaré el café de la tarde por una infusión de manzanilla o té verde (sin azúcar).']
}, {
  id: 6,
  title: 'ANCLA EMOCIONAL',
  subtitle: 'El "Botón de Paz"',
  icon: Smile,
  objective: 'Manejar la irritabilidad y el pánico del "fogonazo" cuando estás en público.',
  concept: 'Antes del sofoco físico, suele venir un "aura" de ansiedad. Si te asustas o te avergüenzas, la adrenalina hará que el sudor sea peor. Tienes que aprender a ser una "observadora técnica" de tu cuerpo.',
  strategy: {
    name: 'Técnica de la Frase Escudo',
    text: 'Cuando sientas el calor subiendo, di mentalmente: "Es solo mi cuerpo recalibrando, pasará en 60 segundos". No luches contra el calor, deja que te atraviese. Al no resistirte, el sistema nervioso se relaja más rápido.'
  },
  checklist: ['Identificar mi situación de mayor estrés y tener mi botella de agua fría a mano.', 'Perdonar mis momentos de mal humor; son mis hormonas, no soy yo.', 'Escribir 3 cosas por las que estoy agradecida antes de dormir para bajar el cortisol.']
}];
export const Chapters = () => <section className="py-20 bg-cream" data-id="element-51">
    <div className="max-w-4xl mx-auto px-6" data-id="element-52">
      <div className="text-center mb-16" data-id="element-53">
        <h2 className="text-4xl md:text-5xl font-playfair font-bold text-brown mb-4" data-id="element-54">
          El Protocolo Paso a Paso
        </h2>
        <p className="text-xl text-brown/70" data-id="element-55">
          Lo que encontrarás dentro de la guía:
        </p>
      </div>

      <div className="space-y-12" data-id="element-56">
        {chaptersData.map((chapter, index) => <FadeIn key={chapter.id} delay={index * 0.1} data-id="element-57">
            <div className={`rounded-3xl overflow-hidden shadow-sm border border-terracotta/10 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9F0E5]'}`} data-id="element-58">
              <div className="p-8 md:p-10" data-id="element-59">
                <div className="flex items-center gap-4 mb-6" data-id="element-60">
                  <div className="w-14 h-14 rounded-2xl bg-terracotta/10 flex items-center justify-center text-terracotta shrink-0" data-id="element-61">
                    <chapter.icon className="w-7 h-7" data-id="element-62" />
                  </div>
                  <div data-id="element-63">
                    <h3 className="text-sm font-bold tracking-widest text-terracotta uppercase mb-1" data-id="element-64">
                      Capítulo {chapter.id}
                    </h3>
                    <h4 className="text-2xl md:text-3xl font-playfair font-bold text-brown" data-id="element-65">
                      {chapter.title} <span className="text-brown/50" data-id="element-66">—</span>{' '}
                      <span className="italic" data-id="element-67">{chapter.subtitle}</span>
                    </h4>
                  </div>
                </div>

                <div className="space-y-6 text-brown/80" data-id="element-68">
                  <p data-id="element-69">
                    <strong data-id="element-70">Objetivo:</strong> {chapter.objective}
                  </p>
                  <p data-id="element-71">
                    <strong data-id="element-72">El Concepto:</strong> {chapter.concept}
                  </p>

                  <div className="bg-white/60 p-6 rounded-xl border border-white" data-id="element-73">
                    <h5 className="font-bold text-terracotta mb-2" data-id="element-74">
                      {chapter.strategy.name}
                    </h5>
                    <p className="whitespace-pre-line" data-id="element-75">
                      {chapter.strategy.text}
                    </p>

                    {chapter.strategy.bad && <div className="mt-4 space-y-2 text-sm" data-id="element-76">
                        <p className="text-red-600 flex items-center gap-2" data-id="element-77">
                          <span className="font-bold" data-id="element-78">Mal:</span>{' '}
                          {chapter.strategy.bad}
                        </p>
                        <p className="text-green-700 flex items-center gap-2" data-id="element-79">
                          <span className="font-bold" data-id="element-80">Bien:</span>{' '}
                          {chapter.strategy.good}
                        </p>
                      </div>}
                  </div>

                  {chapter.insight && <div className="flex gap-3 items-start bg-gold/10 p-4 rounded-lg text-brown-light" data-id="element-81">
                      <span className="text-xl" data-id="element-82">💡</span>
                      <p data-id="element-83">
                        <strong data-id="element-84">Key Insight:</strong> {chapter.insight}
                      </p>
                    </div>}

                  <div data-id="element-85">
                    <h5 className="font-bold text-brown mb-3 flex items-center gap-2" data-id="element-86">
                      <CheckCircle2 className="w-5 h-5 text-terracotta" data-id="element-87" />
                      Checklist de Acción
                    </h5>
                    <ul className="space-y-3" data-id="element-88">
                      {chapter.checklist.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-89">
                          <div className="w-5 h-5 rounded border-2 border-terracotta/40 mt-0.5 shrink-0 flex items-center justify-center" data-id="element-90">
                            <div className="w-2.5 h-2.5 bg-terracotta rounded-sm opacity-0 hover:opacity-100 transition-opacity cursor-pointer" data-id="element-91"></div>
                          </div>
                          <span className="text-sm md:text-base" data-id="element-92">{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  {chapter.worksheet && <div className="mt-6 border-2 border-dashed border-terracotta/30 rounded-xl p-6 bg-white" data-id="element-93">
                      <h5 className="font-bold text-center mb-4 flex items-center justify-center gap-2" data-id="element-94">
                        <BookOpen className="w-5 h-5 text-terracotta" data-id="element-95" />
                        Hoja de Trabajo: {chapter.worksheet.title}
                      </h5>
                      <div className="space-y-4" data-id="element-96">
                        {chapter.worksheet.fields.map((field, i) => <div key={i} data-id="element-97">
                            <label className="block text-sm font-medium mb-2" data-id="element-98">
                              {field}
                            </label>
                            <div className="w-full h-10 border-b border-brown/20 bg-cream/30" data-id="element-99"></div>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </FadeIn>)}
      </div>
    </div>
  </section>;